import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BookButton, SecondaryButton } from "@/components/CtaButtons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINES = ["Employee Relations", "People Risk &", "High-Stakes", "Case Advisory."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.3 + i * 0.12, ease: [0.76, 0, 0.24, 1] } }),
};

export function Hero() {
  const ref = useRef(null);
  const [spot, setSpot] = useState({ x: "50%", y: "35%" });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setSpot({ x: `${((e.clientX - r.left) / r.width) * 100}%`, y: `${((e.clientY - r.top) / r.height) * 100}%` });
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <motion.div aria-hidden initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
      <div className="absolute inset-0 bg-ink/82" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(600px circle at ${spot.x} ${spot.y}, rgba(56,163,166,0.18), transparent 60%)` }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-[11px] tracking-[0.35em] text-gold uppercase mb-8" data-testid="hero-eyebrow">
          Employee Relations · People Risk · High-Stakes Cases
        </motion.p>

        <h1 className="font-serif font-light text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-7xl tracking-tight max-w-5xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">{line}</motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 text-bone/90 text-lg md:text-xl max-w-2xl leading-relaxed font-light" data-testid="hero-positioning">
          I help HR leaders navigate complex employee relations cases, manage people risk and build scalable ER frameworks.
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 1 }}
          className="mt-4 text-muted2 text-base max-w-xl leading-relaxed" data-testid="hero-supporting">
          Independent senior support for sensitive cases today — and stronger ER capability for tomorrow.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 1 }}
          className="mt-10 flex flex-col gap-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <SecondaryButton to="/sensitive-case-advisory" label="Discuss a Sensitive ER Case" size="lg" testid="hero-case-secondary" />
            <BookButton reason="Complex / Sensitive ER Case" size="lg" testid="hero-case-book" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <SecondaryButton to="/employee-relations-framework" label="Strengthen Your ER Framework" size="lg" testid="hero-framework-secondary" />
            <BookButton reason="ER Framework / Capability" size="lg" testid="hero-framework-book" />
          </div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted2">
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
